"use client";

import { useEffect, useState } from "react";
import {
  Pencil,
  Check,
  TriangleAlert,
  Clock,
  Globe,
  Lock,
  Loader2,
  ChevronRight,
} from "lucide-react";
import { apiClient, isPaidRequiredError, type AlertDoc } from "@/lib/api-client";
import {
  IcyDialogShell,
  PrimaryDialogButton,
  SecondaryDialogButton,
  getDialogInputClass,
  DIALOG_LABEL_CLASS,
  DIALOG_ERROR_CLASS,
} from "./IcyDialogShell";
import { EditScheduleDialog } from "./EditScheduleDialog";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * RenameAlertDialog ("Edit details") — single hub to edit alert configuration:
 * name, notification email, schedule, and visibility.
 *
 * Why one modal: the alert page body is now a generated plain-English
 * briefing, not an editable settings form. Name + email save together via
 * their PATCH endpoints (only dirty fields hit the network).
 * Visibility toggles inline. Schedule opens the dedicated preset/AI editor on
 * top because that flow is rich enough to deserve its own surface.
 */
export function RenameAlertDialog({
  open,
  onOpenChange,
  alert,
  onSaved,
  onPaidRequired,
  /** Owner/paid gate. When false, schedule + visibility edits route to the
   *  paywall instead of mutating. Defaults true to preserve prior behavior. */
  canMutate = true,
  viewMode,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  alert: AlertDoc;
  onSaved: () => void;
  onPaidRequired?: () => void;
  canMutate?: boolean;
  viewMode?: string;
}) {
  const [name, setName] = useState(alert.name);
  const [email, setEmail] = useState(alert.notification_email ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [visibilityBusy, setVisibilityBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setName(alert.name);
      setEmail(alert.notification_email ?? "");
      setError(null);
    }
  }, [open, alert.name, alert.notification_email]);

  const isPublic = Boolean(alert.is_public);

  const nameDirty = name.trim() !== (alert.name ?? "").trim();
  const emailDirty =
    email.trim().toLowerCase() !== (alert.notification_email ?? "").trim().toLowerCase();
  const dirty = nameDirty || emailDirty;

  const nameInvalid = name.trim().length === 0;
  const emailInvalid = email.trim().length > 0 && !EMAIL_RE.test(email.trim());

  const handleSave = async () => {
    if (!dirty || nameInvalid || emailInvalid) return;
    setSaving(true);
    setError(null);
    try {
      if (nameDirty) await apiClient.updateAlertName(alert._id, name.trim());
      if (emailDirty)
        await apiClient.updateAlertEmail(alert._id, email.trim().toLowerCase());
      onSaved();
      onOpenChange(false);
    } catch (err: any) {
      if (isPaidRequiredError(err)) {
        onOpenChange(false);
        onPaidRequired?.();
        return;
      }
      setError(err?.message || "Could not save changes");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleVisibility = async () => {
    if (!canMutate) {
      onOpenChange(false);
      onPaidRequired?.();
      return;
    }
    setVisibilityBusy(true);
    setError(null);
    try {
      await apiClient.updateAlertVisibility(alert._id, !isPublic);
      onSaved();
    } catch (err: any) {
      if (isPaidRequiredError(err)) {
        onOpenChange(false);
        onPaidRequired?.();
        return;
      }
      setError(err?.message || "Could not change visibility");
    } finally {
      setVisibilityBusy(false);
    }
  };

  return (
    <>
      <IcyDialogShell viewMode={viewMode}
        open={open}
        onOpenChange={onOpenChange}
        title="Edit details"
        subtitle="Name, schedule, email and visibility"
        Icon={Pencil}
        size="md"
        footer={
          <>
            <SecondaryDialogButton
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Cancel
            </SecondaryDialogButton>
            <PrimaryDialogButton variant={viewMode === "signal" ? "emerald" : "icy"}
              onClick={handleSave}
              busy={saving}
              disabled={!dirty || nameInvalid || emailInvalid || saving}
              Icon={Check}
            >
              Save
            </PrimaryDialogButton>
          </>
        }
      >
        <div>
          <label className={DIALOG_LABEL_CLASS} htmlFor="alert-name">
            Alert name
          </label>
          <input
            id="alert-name"
            className={getDialogInputClass(viewMode)}
            value={name}
            maxLength={120}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. TSLA flow + news"
            autoFocus
          />
        </div>

        <div>
          <label className={DIALOG_LABEL_CLASS} htmlFor="alert-email">
            Notification email
          </label>
          <input
            id="alert-email"
            type="email"
            className={getDialogInputClass(viewMode)}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
          />
          {emailInvalid && (
            <div className={DIALOG_ERROR_CLASS}>
              <TriangleAlert className="w-3.5 h-3.5" />
              <span>Enter a valid email address.</span>
            </div>
          )}
        </div>

        {/* Schedule — opens the dedicated preset/AI editor on top */}
        <div>
          <label className={DIALOG_LABEL_CLASS}>Schedule</label>
          <button
            type="button"
            onClick={() => setScheduleOpen(true)}
            className="group flex w-full items-center gap-2 rounded-md border border-slate-300/15 bg-slate-950/40 px-3 py-2.5 text-left hover:border-slate-300/35 hover:bg-slate-900/60 transition-colors"
          >
            <Clock className="w-4 h-4 text-slate-400 shrink-0" />
            <span className="min-w-0 flex-1 truncate text-[12.5px] text-slate-100/90">
              {alert.schedule_description || alert.schedule || "No schedule set"}
            </span>
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-sky-300 shrink-0">
              Change <ChevronRight className="w-3.5 h-3.5" />
            </span>
          </button>
        </div>

        {/* Visibility — inline toggle */}
        <div>
          <label className={DIALOG_LABEL_CLASS}>Visibility</label>
          <button
            type="button"
            onClick={handleToggleVisibility}
            disabled={visibilityBusy}
            className="flex w-full items-center gap-2 rounded-md border border-slate-300/15 bg-slate-950/40 px-3 py-2.5 text-left hover:border-slate-300/35 hover:bg-slate-900/60 transition-colors disabled:opacity-60"
          >
            {isPublic ? (
              <Globe className="w-4 h-4 text-slate-400 shrink-0" />
            ) : (
              <Lock className="w-4 h-4 text-slate-400 shrink-0" />
            )}
            <span className="min-w-0 flex-1 truncate text-[12.5px] text-slate-100/90">
              {isPublic ? "Public — discoverable by others" : "Private — only you"}
            </span>
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-sky-300 shrink-0">
              {visibilityBusy ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : isPublic ? (
                "Make private"
              ) : (
                "Make public"
              )}
            </span>
          </button>
        </div>

        {error && (
          <div className={DIALOG_ERROR_CLASS}>
            <TriangleAlert className="w-3.5 h-3.5" />
            <span>{error}</span>
          </div>
        )}
      </IcyDialogShell>

      <EditScheduleDialog
        open={scheduleOpen}
        onOpenChange={setScheduleOpen}
        alert={alert}
        canMutate={canMutate}
        onSaved={onSaved}
        onPaidRequired={onPaidRequired}
      />
    </>
  );
}
